import {
    cmpEncryptionChangePermissions,
    cmpEncryptionChangePermissionsDescription,
} from './modifyProtectionInfoBarCustomAction.locstring.json';
import { cancelButton } from 'owa-locstrings/lib/strings/cancelbutton.locstring.json';
import { okButton } from 'owa-locstrings/lib/strings/okbutton.locstring.json';
import loc from 'owa-localize';
import setIRMData from '../../actions/rms/setIRMData';
import ProtectionViewState from 'owa-mail-protection-types/lib/schema/ProtectionViewState';
import { InfoBarHostViewState } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import { protectionStore } from 'owa-mail-protection/lib/store/protectionStore';
import { observer } from 'mobx-react-lite';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Dialog, DialogFooter, DialogType } from '@fluentui/react/lib/Dialog';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { OwaModalProps, showModal } from 'owa-modal';
import clearClassification from '../classification/clearClassification';
import classifyMessage from '../classification/classifyMessage';
import getEncryptionInfoBarMessageId from './getEncryptionInfoBarMessageId';
import removeInfoBarMessage from 'owa-info-bar/lib/actions/removeInfoBarMessage';
import protectMessage from './protectMessage';
import BodyType from 'owa-service/lib/contract/BodyType';

import * as React from 'react';

export default async function showModifyProtectionDialog(
    protectionViewState: ProtectionViewState,
    infoBarViewState: InfoBarHostViewState,
    bodyType: BodyType,
    targetWindow: Window
): Promise<boolean> {
    const templates = protectionStore.rmsTemplates;
    const classifications = protectionStore.messageClassifications;
    const { classificationViewState, IRMData } = protectionViewState;

    //TODO: Replace with default if exists.
    let defaultSelection = IRMData ? IRMData.RmsTemplateId : null;

    let dropdownOptions: IDropdownOption[] = [...templates.entries()].map(entry => ({
        key: entry[0],
        text: entry[1].TemplateName,
    }));

    if (classificationViewState) {
        const { messageClassification } = classificationViewState;
        if (!defaultSelection && messageClassification) {
            defaultSelection = messageClassification.Id;
        }

        const classificationDropdownOptions: IDropdownOption[] = [...classifications.entries()].map(
            entry => ({
                key: entry[0],
                text: entry[1].Name,
            })
        );

        dropdownOptions = classificationDropdownOptions.concat(dropdownOptions);
    }

    const WrappedComponent = ({ onDismiss }: OwaModalProps<string>) => (
        <SelectEncryptionTemplateModal
            defaultSelectedKey={defaultSelection}
            options={dropdownOptions}
            onDismiss={onDismiss}
        />
    );

    const [modalPromise] = showModal(WrappedComponent, targetWindow);
    return modalPromise.then(newTemplate => {
        if (newTemplate) {
            if (templates.get(newTemplate)) {
                clearClassification(classificationViewState, infoBarViewState);
                return protectMessage(protectionViewState, infoBarViewState, newTemplate, bodyType);
            } else if (classifications.get(newTemplate)) {
                if (IRMData?.RmsTemplateId) {
                    removeInfoBarMessage(
                        infoBarViewState,
                        getEncryptionInfoBarMessageId(IRMData.RmsTemplateId)
                    );
                }
                setIRMData(protectionViewState, null);
                classifyMessage(
                    classificationViewState,
                    infoBarViewState,
                    classifications.get(newTemplate)
                );
                return true;
            }
        }
        return false;
    });
}

interface SelectEncryptionTemplateProps extends OwaModalProps<string> {
    defaultSelectedKey: string;
    options: IDropdownOption[];
}

const SelectEncryptionTemplateModal = observer(function SelectEncryptionTemplateModal(
    props: SelectEncryptionTemplateProps
) {
    const [template, setTemplate] = React.useState(props.defaultSelectedKey);
    const dismissEmpty = React.useCallback(() => {
        props.onDismiss(null);
    }, [props.onDismiss]);

    const dismissWithTarget = React.useCallback(() => {
        props.onDismiss(template);
    }, [props.onDismiss, template]);

    const handleClick = React.useCallback(
        (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) => {
            setTemplate(item.key.toString());
        },
        []
    );

    return (
        <Dialog
            hidden={false}
            dialogContentProps={{
                type: DialogType.normal,
                title: loc(cmpEncryptionChangePermissions),
                subText: loc(cmpEncryptionChangePermissionsDescription),
            }}
            onDismiss={dismissEmpty}
            minWidth={'388px'}
            maxWidth={'inherit'}>
            <Dropdown
                defaultSelectedKey={props.defaultSelectedKey}
                onChange={handleClick}
                options={props.options}
            />
            <DialogFooter>
                <PrimaryButton text={loc(okButton)} onClick={dismissWithTarget} />
                <DefaultButton text={loc(cancelButton)} onClick={dismissEmpty} />
            </DialogFooter>
        </Dialog>
    );
});
